import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowRight, Map } from 'lucide-react';
import { AI_EMPLOYEES, EMPLOYEE_CATEGORIES } from '../data/employees';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

export default function Employees() {
  const { user } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Filter employees
  const filteredEmployees = AI_EMPLOYEES.filter((employee) => {
    const matchesSearch =
      employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = !selectedCategory || employee.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // All employees are now available to all users
  const isEmployeeAvailable = () => true;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>AI Employees</h1>
          <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>
            Choose an AI employee to start a conversation and get expert help
          </p>
        </div>
        <Link
          to="/roadmap"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-colors shrink-0 ${
            isDark
              ? 'bg-accent-purple/20 text-accent-purple hover:bg-accent-purple/30'
              : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
          }`}
        >
          <Map className="w-4 h-4" />
          Go to Roadmap
        </Link>
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        variants={fadeInUp}
        className="flex flex-col sm:flex-row gap-4"
      >
        {/* Search */}
        <div className="relative flex-1">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
          <input
            type="text"
            placeholder="Search employees..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-12 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-800 border-border text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
          />
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
              !selectedCategory
                ? 'bg-gradient-accent text-white'
                : isDark ? 'bg-dark-800 text-text-secondary hover:bg-dark-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            All
          </button>
          {EMPLOYEE_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-gradient-accent text-white'
                  : isDark ? 'bg-dark-800 text-text-secondary hover:bg-dark-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Employees Grid */}
      <motion.div
        variants={staggerContainer}
        className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        {filteredEmployees.map((employee) => {
          const available = isEmployeeAvailable();
          const isComingSoon = employee.isComingSoon;

          // Card content - shared between Link and div
          const cardContent = (
            <>
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div
                  className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center ${!isComingSoon ? 'group-hover:scale-110' : ''} transition-transform ${isComingSoon ? 'opacity-70' : ''}`}
                >
                  <span className="text-xl sm:text-2xl">
                    {employee.avatar}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {isComingSoon ? (
                    <span className="badge bg-amber-500/20 text-amber-500 text-xs">
                      Coming Soon
                    </span>
                  ) : employee.isNew ? (
                    <span className="badge bg-accent-green/20 text-accent-green text-xs">
                      NEW
                    </span>
                  ) : null}
                </div>
              </div>

              <h3 className={`text-base sm:text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'} ${isComingSoon ? 'opacity-70' : ''}`}>
                {employee.name}
              </h3>
              <p className={`text-sm mb-2 sm:mb-3 ${isComingSoon ? 'text-amber-500/80' : 'text-accent-cyan'}`}>{employee.role}</p>

              {/* Description - Hidden on mobile */}
              <p className={`hidden sm:block text-sm line-clamp-2 mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'} ${isComingSoon ? 'opacity-70' : ''}`}>
                {employee.description}
              </p>

              {/* Capabilities Preview - Hidden on mobile */}
              <div className={`hidden sm:flex flex-wrap gap-2 mb-4 ${isComingSoon ? 'opacity-70' : ''}`}>
                {employee.capabilities.slice(0, 3).map((cap) => (
                  <span
                    key={cap}
                    className={`px-2 py-1 rounded-lg text-xs ${isDark ? 'bg-dark-700 text-text-muted' : 'bg-slate-100 text-slate-500'}`}
                  >
                    {cap}
                  </span>
                ))}
              </div>

              {/* Action */}
              <div className={`flex items-center justify-between pt-3 sm:pt-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  {isComingSoon ? 'Available soon' : 'Start chatting'}
                </span>
                {!isComingSoon && (
                  <ArrowRight className={`w-4 h-4 group-hover:text-accent-purple group-hover:translate-x-1 transition-all ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                )}
              </div>
            </>
          );

          return (
            <motion.div key={employee.id} variants={fadeInUp}>
              {isComingSoon ? (
                <div
                  className={`block h-full p-4 sm:p-5 rounded-2xl border cursor-default ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}
                >
                  {cardContent}
                </div>
              ) : (
                <Link
                  to={`/dashboard/employees/${employee.id}`}
                  className={`block h-full p-4 sm:p-5 rounded-2xl border transition-all duration-200 group ${isDark ? 'bg-dark-800 border-border hover:border-white/10' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'}`}
                >
                  {cardContent}
                </Link>
              )}
            </motion.div>
          );
        })}
      </motion.div>

      {/* Empty State */}
      {filteredEmployees.length === 0 && (
        <motion.div
          variants={fadeInUp}
          className="text-center py-16"
        >
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-dark-800' : 'bg-slate-100'}`}>
            <Search className={`w-8 h-8 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            No employees found
          </h3>
          <p className={`mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
            Try adjusting your search or filter criteria
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory(null);
            }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${isDark ? 'bg-dark-800 text-white hover:bg-dark-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            Clear filters
          </button>
        </motion.div>
      )}
    </motion.div>
  );
}
